import React, { useState } from 'react';
import { Button, Checkbox, Form, Input, Alert, message, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { UserOutlined, MailOutlined, LockOutlined, UserAddOutlined } from '@ant-design/icons';
import { signup } from '../../features/auth/authSlice';
import { useNavigate, Link } from 'react-router-dom';

const { Title, Text } = Typography;

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error, loading } = useSelector((state) => state.auth);
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  const handleSubmit = (values) => {
    // Ensure passwords match before dispatching the signup action
    if (credentials.password !== credentials.confirmPassword) {
      message.error('Passwords do not match!');
      return;
    }
    
    dispatch(signup(credentials))
      .unwrap() // Use unwrap to handle the resolved value or error
      .then(() => {
        message.success('Registration successful!');
        navigate('/'); // Redirect to home page on successful registration
      })
      .catch(() => {
        message.error(error?.message || 'Registration failed. Please try again.');
      });
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleFocus = () => {
    if (error) {
      dispatch({ type: 'auth/clearError' });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserAddOutlined className="text-2xl text-white" />
        </div>
        <Title level={2} className="mb-2 text-slate-800">Create Account</Title>
        <Text className="text-slate-600">Join us today and get started</Text>
      </div>

      <Form
        onFinish={handleSubmit}
        name="signup"
        layout="vertical"
        autoComplete="on"
        size="large"
        className="space-y-1"
      >
        {error && (
          <Alert 
            className="mb-6" 
            message={error} 
            type="error" 
            showIcon 
            closable 
          />
        )}

        <Form.Item
          label={<span className="text-slate-700 font-medium">Full Name</span>}
          name="name"
          rules={[
            { required: true, message: 'Please input your full name!' },
            { min: 2, message: 'Name must be at least 2 characters!' }
          ]}
        >
          <Input
            name="name"
            prefix={<UserOutlined className="text-slate-400" />}
            placeholder="Enter your full name"
            value={credentials.name}
            onChange={handleChange}
            onFocus={handleFocus}
            className="rounded-lg h-12"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-slate-700 font-medium">Email Address</span>}
          name="email"
          rules={[
            { required: true, message: 'Please input your email!' },
            { type: 'email', message: 'Please enter a valid email!' }
          ]}
        >
          <Input
            name="email"
            prefix={<MailOutlined className="text-slate-400" />}
            placeholder="Enter your email"
            value={credentials.email}
            onChange={handleChange}
            onFocus={handleFocus}
            className="rounded-lg h-12"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-slate-700 font-medium">Password</span>}
          name="password"
          rules={[
            { required: true, message: 'Please input your password!' },
            { min: 6, message: 'Password must be at least 6 characters!' }
          ]}
        >
          <Input.Password
            name="password"
            prefix={<LockOutlined className="text-slate-400" />}
            placeholder="Create a password"
            value={credentials.password}
            onChange={handleChange}
            onFocus={handleFocus}
            className="rounded-lg h-12"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-slate-700 font-medium">Confirm Password</span>}
          name="confirmPassword"
          rules={[
            { required: true, message: 'Please confirm your password!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match!'));
              },
            }),
          ]}
        >
          <Input.Password
            name="confirmPassword"
            prefix={<LockOutlined className="text-slate-400" />}
            placeholder="Confirm your password"
            value={credentials.confirmPassword}
            onChange={handleChange}
            onFocus={handleFocus}
            className="rounded-lg h-12"
          />
        </Form.Item>

        <Form.Item
          name="terms"
          valuePropName="checked"
          rules={[
            { 
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions!')),
            },
          ]}
          className="mb-6"
        >
          <Checkbox className="text-slate-600">
            I agree to the{' '}
            <a href="/privacy" className="text-blue-500 hover:text-blue-600">
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-500 hover:text-blue-600">
              Privacy Policy
            </a>
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Button
            type="primary"
            loading={loading}
            htmlType="submit"
            icon={<UserAddOutlined />}
            className="w-full h-12 bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 rounded-lg font-medium text-base"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </Form.Item>

        <div className="text-center">
          <Text className="text-slate-600">Already have an account? </Text>
          <Link to="/login" className="text-blue-500 hover:text-blue-600 font-medium">
            Sign In Here
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default SignUp;